import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Banner,
  Breadcrumbs,
  Empty,
  LayerCard,
  Loader,
  Table,
  Tabs,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { ClockCounterClockwiseIcon, HouseIcon, UsersIcon } from '@phosphor-icons/react'
import { useHasRole } from '../auth/useRoles'
import { apiRequest, ApiError } from '../api/client'
import {
  getCoworker,
  updateCoworkerProfile,
  type UpdateCoworkerProfileInput,
} from '../api/coworkers'
import {
  CoworkerEditForm,
  type CoworkerEditFormPatch,
  type CoworkerEditFormValue,
} from '../components/coworker/CoworkerEditForm'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import {
  OCSF_ACTIVITY_LABEL,
  OCSF_STATUS_LABEL,
  type Coworker,
  type OcsfQueryRow,
} from '@hub-wifi/shared'

type CoworkerEditTab = 'profile' | 'history'

interface AuditEventsResponse {
  events: OcsfQueryRow[]
}

function toFormValue(coworker: Coworker): CoworkerEditFormValue {
  return {
    name: coworker.name,
    email: coworker.email,
    mobilePhone: coworker.mobilePhone,
    cpf: coworker.cpf,
    taxIdCountry: coworker.taxIdCountry,
    taxIdType: coworker.taxIdType,
    taxIdValue: coworker.taxIdValue,
    birthDate: coworker.birthDate,
  }
}

/** Admin/operator coworker profile edit — route `/coworkers/:id/edit`. */
export function CoworkerEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [coworker, setCoworker] = useState<Coworker | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<CoworkerEditTab>('profile')

  // The access-history tab reads `/api/audit/events`, which is admin-only.
  // Operators can still edit coworkers, so hide the tab for them rather
  // than letting the fetch 403 into an error banner.
  const canReadAuditHistory = useHasRole('admin')

  const [historyEvents, setHistoryEvents] = useState<OcsfQueryRow[]>([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      if (!id) return
      try {
        const token = await getAccessTokenSilently()
        const result = await getCoworker(id, token)
        setCoworker(result)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [id, getAccessTokenSilently, t])

  // Access history — this coworker's own audit trail, e.g. door-access
  // events (Task 8). Fetched alongside the profile, independent of which
  // tab is active.
  useEffect(() => {
    async function fetchHistory() {
      if (!id) return
      if (!canReadAuditHistory) {
        setHistoryLoading(false)
        return
      }
      setHistoryLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const result = await apiRequest<AuditEventsResponse>(
          `/api/audit/events?actor_uid=${id}&limit=100`,
          { token },
        )
        setHistoryEvents(result.events)
        setHistoryError(null)
      } catch (err) {
        setHistoryError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
      } finally {
        setHistoryLoading(false)
      }
    }
    void fetchHistory()
  }, [id, getAccessTokenSilently, canReadAuditHistory])

  const handleSave = useCallback(
    async (patch: CoworkerEditFormPatch) => {
      if (!id) return
      try {
        const token = await getAccessTokenSilently()
        const body: UpdateCoworkerProfileInput = patch
        await updateCoworkerProfile(id, body, token)
        toasts.add({ variant: 'success', title: t('coworkerEdit.updateSuccess') })
        navigate('/coworkers')
      } catch (err) {
        toasts.add({
          variant: 'error',
          title: t('common.errorTitle'),
          description: err instanceof ApiError ? err.message : t('common.error'),
        })
      }
    },
    [id, getAccessTokenSilently, navigate, t, toasts],
  )

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<UsersIcon size={14} />} href="/coworkers">
              {t('coworkers.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('coworkerEdit.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('coworkerEdit.title')}
        description={t('coworkerEdit.description')}
      />

      <div className="mb-3">
        <Tabs
          tabs={[
            { value: 'profile', label: t('coworkerEdit.tabs.profile') },
            ...(canReadAuditHistory
              ? [{ value: 'history', label: t('coworkerEdit.tabs.history') }]
              : []),
          ]}
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as CoworkerEditTab)}
        />
      </div>

      {activeTab === 'profile' && (
        <>
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader size="lg" />
            </div>
          )}

          {!loading && error && (
            <Banner variant="error">
              {t('common.errorPrefix')} {error}
            </Banner>
          )}

          {!loading && !error && coworker && (
            <LayerCard>
              <LayerCard.Primary>
                <CoworkerEditForm
                  value={toFormValue(coworker)}
                  onSave={handleSave}
                  coworkerId={coworker.id}
                />
              </LayerCard.Primary>
            </LayerCard>
          )}
        </>
      )}

      {activeTab === 'history' && canReadAuditHistory && (
        <>
          {historyLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader size="lg" />
            </div>
          )}

          {!historyLoading && historyError && (
            <Banner variant="error">
              {t('common.errorPrefix')}{' '}
              {historyError === 'GENERIC_ERROR' ? t('common.error') : historyError}
            </Banner>
          )}

          {!historyLoading && !historyError && historyEvents.length === 0 && (
            <Empty
              icon={<ClockCounterClockwiseIcon size={48} className="text-kumo-inactive" />}
              title={t('coworkerEdit.history.empty')}
            />
          )}

          {!historyLoading && !historyError && historyEvents.length > 0 && (
            <LayerCard>
              <LayerCard.Primary className="p-0">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head>{t('coworkerEdit.history.column.timestamp')}</Table.Head>
                        <Table.Head>{t('coworkerEdit.history.column.activity')}</Table.Head>
                        <Table.Head>{t('coworkerEdit.history.column.status')}</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {historyEvents.map((evt) => (
                        <Table.Row key={evt.uid}>
                          <Table.Cell>{new Date(evt.time).toLocaleString()}</Table.Cell>
                          <Table.Cell>
                            {OCSF_ACTIVITY_LABEL[evt.envelope.activity_id] ??
                              evt.envelope.activity_id}
                          </Table.Cell>
                          <Table.Cell>
                            {OCSF_STATUS_LABEL[evt.envelope.status_id] ?? evt.envelope.status_id}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </LayerCard.Primary>
            </LayerCard>
          )}
        </>
      )}
    </div>
  )
}
